/**
 * Idempotently inject a Google Fonts <link> into <head> so the canvas preview can
 * render the chosen family. The Creatomate render fetches its own fonts server-side;
 * this util exists purely so the editor preview matches what the user will see.
 *
 * Safe to call repeatedly with the same family — duplicates are skipped via a
 * stable element id. Multiple weights are bundled into one stylesheet per family.
 */

const LINK_ID_PREFIX = 'ugc-google-font-';

const ALREADY_REQUESTED = new Set<string>();

const familyToHref = (family: string): string => {
  // Request the regular and bold weights we actually use in the editor.
  const encoded = family.trim().replace(/\s+/g, '+');
  return `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;700;900&display=swap`;
};

export const loadGoogleFont = (family: string | undefined | null): void => {
  if (typeof document === 'undefined') return;
  const f = (family ?? '').trim();
  if (!f) return;
  if (ALREADY_REQUESTED.has(f)) return;
  ALREADY_REQUESTED.add(f);

  const id = LINK_ID_PREFIX + f.toLowerCase().replace(/\s+/g, '-');
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = familyToHref(f);
  document.head.appendChild(link);
};
